/**
 * Format: Algolia
 * -----------------------------------------------------------------------------
 * Formats Algolia responses.
 *
 */
import { formatPriceAmount, publishedProduct } from '~/helpers/format'

/**
 * Format Algolia product data.
 * @param {Object} response - Algolia item data.
 * @returns {Object}
 */
export function formatAlgoliaProduct(response) {
  const product = response

  const url = `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`
  const publishedTimestamp = product.meta['hc-defined']?.publishdtimestamp || ''

  const formattedProduct = {

    /**
     * Spread the original product data from Algolia into the object.
     * - This ensures that the Algolia product data required for insights
     *   tracking is retained.
     */
    ...product,
    available:
      product.inventory_available || product.inventory_management === null,
    compareAtPrice: formatAlgoliaCompareAtPrice(product),
    description: product.body_html_safe,
    handle: product.handle,
    id: product.id,
    images: formatAlgoliaImages(product),
    options: null,
    price: formatAlgoliaPrice(product),
    tags: formatAlgoliaTags(product),
    title: product.title,
    type: product.product_type,
    url,
    variantId: product.objectID ? Number(product.objectID) : false,
    vendor: product.vendor,
    hyphenatedIsbn: product.meta['hc-defined']?.hyphenatedIsbn || '',
    publishedProduct: publishedProduct(publishedTimestamp),
    hasOverrideCTA: getAlgoliaOverrideCTA(product),
  }

  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.secondaryImage = {}
  formattedProduct.variants = []

  const firstAvailableVariant = {
    available: formattedProduct.available,
    id: formattedProduct.variantId,
  }

  formattedProduct.firstAvailableVariant = firstAvailableVariant

  return formattedProduct
}

/**
 * Format Algolia product price.
 * @param {Object} product - Algolia item data.
 * @returns {Object}
 */
function formatAlgoliaPrice(product) {
  return {
    amount: formatPriceAmount(product?.variants_min_price),
    currencyCode: window.cnvs.store.currency,
    max: formatPriceAmount(product?.variants_max_price),
    min: formatPriceAmount(product?.variants_min_price),
    varies: product.variants_max_price !== product.variants_min_price,
  }
}

/**
 * Format Algolia product price.
 * @param {Object} product - Algolia item data.
 * @returns {Object}
 */
function formatAlgoliaCompareAtPrice(product) {
  return {
    amount: formatPriceAmount(product.compare_at_price),
    currencyCode: window.cnvs.store.currency,
    max: formatPriceAmount(product.variants_compare_at_price_max),
    min: formatPriceAmount(product.variants_compare_at_price_min),
    varies:
      product.variants_compare_at_price_max !==
      product.variants_compare_at_price_min,
  }
}

/**
 * Format Algolia image data.
 * @param {Object} product - Algolia item data.
 * @returns {Array}
 */
function formatAlgoliaImages(product) {
  return [
    {
      alt: '',
      index: 0,
      src: product.image,
    },
  ]
}

/**
 * Format the product tags from Algolia.
 * @param {Object} product - Algolia item data.
 * @returns {Array}
 */
function formatAlgoliaTags(product) {
  const tags = [...product.tags]
  const namedTags = namedTagsToTags(product)
  const allTags = tags.concat(namedTags)

  return allTags
}

/**
 * Convert Algolia's named tags to tags.
 * - Loops through the `named_tags` object and restores the tags back to simple
 *   text tags as entered in the Shopify admin.
 * @param {Object} product - Algolia item data.
 * @returns {Array}
 */
function namedTagsToTags(product) {
  const convertedTags = []

  Object.entries(product.named_tags).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((arrayValue) => {
        convertedTags.push(`${key}:${arrayValue}`)
      })

      return
    }

    convertedTags.push(`${key}:${value}`)
  })

  return convertedTags
}

/**
 * Check if the product has an overridden CTA on the PDP.
 * @param {Object} product - Algolia product object.
 * @returns {Boolean} - Whether the product has an overridden CTA.
 */
function getAlgoliaOverrideCTA(product) {
  const productSettings = product?.meta?.['hc-defined']?.productsettings

  if (!productSettings) {
    return false
  }

  try {
    const parsedSettings = JSON.parse(productSettings)
    const isOverrideCTA = parsedSettings?.['override-active']

    return isOverrideCTA
  } catch (error) {
    cnvs.ReportError('Failed to parse productSettings metafield data', error)
    return false
  }
}
